import {
  cloneElement,
  isValidElement,
  type CSSProperties,
  type HTMLAttributes,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react'
import { usePermissions } from './PermissionContext'

type PermissionGateMode = 'disable' | 'hide'

interface ClonableChildProps {
  disabled?: boolean
  'aria-disabled'?: boolean
  style?: CSSProperties
  onClick?: (e: MouseEvent) => void
  [key: string]: unknown
}

interface PermissionGateProps {
  permission?: string
  anyOf?: string[]
  allOf?: string[]
  mode?: PermissionGateMode
  tooltip?: string
  fallback?: ReactNode
  children: ReactElement<ClonableChildProps>
  wrapperProps?: HTMLAttributes<HTMLSpanElement>
}

interface UseGateArgs {
  permission?: string
  anyOf?: string[]
  allOf?: string[]
}

const DEFAULT_TOOLTIP = 'Permisos insuficientes'

function evaluate(
  perms: Set<string>,
  { permission, anyOf, allOf }: UseGateArgs,
): boolean {
  if (permission && !perms.has(permission)) return false
  if (anyOf && anyOf.length > 0 && !anyOf.some(c => perms.has(c))) return false
  if (allOf && allOf.length > 0 && !allOf.every(c => perms.has(c))) return false
  return true
}

// eslint-disable-next-line react-refresh/only-export-components
export function useGate(args: UseGateArgs): boolean {
  const { permissions } = usePermissions()
  return evaluate(permissions, args)
}

export function PermissionGate({
  permission,
  anyOf,
  allOf,
  mode = 'disable',
  tooltip = DEFAULT_TOOLTIP,
  fallback = null,
  children,
  wrapperProps,
}: PermissionGateProps) {
  const { permissions } = usePermissions()
  const allowed = evaluate(permissions, { permission, anyOf, allOf })

  if (allowed) return children

  if (mode === 'hide') return <>{fallback}</>

  if (!isValidElement(children)) return children

  const childProps = (children.props ?? {}) as ClonableChildProps
  const swallowClick = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const disabledChild = cloneElement(children, {
    disabled: true,
    'aria-disabled': true,
    onClick: swallowClick,
    style: {
      ...(childProps.style ?? {}),
      cursor: 'not-allowed',
      opacity: 0.5,
      pointerEvents: 'none',
    },
  })

  return (
    <span
      title={tooltip}
      style={{ display: 'inline-flex' }}
      {...wrapperProps}
    >
      {disabledChild}
    </span>
  )
}
